"use client";

import {
  AgentThreadVo,
  FeedbackSubmitRequest,
  FeedbackSubmitRequestType,
  api,
} from "@/api";
import PlusIcon from "@/assets/icons/plus.svg";
import { ChatBoxFiles } from "@/component/ChatBoxFiles";
import { useFileUpload } from "@/hooks/useFileUpload";
import { fetchUserThreads } from "@/service/threads";
import { consoleCapturer } from "@/util/consoleCapturer";
import { uploadAttachments } from "@/util/uploadAttachments";
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  Select,
  Upload,
  message,
} from "antd";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import ArrowIcon from "./assets/arrow-icon.svg";
import CloseIcon from "./assets/close-icon.svg";

interface FeedbackViewProps {
  onClose?: () => void;
  defaultThreadId?: string;
}

const FEEDBACK_CATEGORIES = [
  { label: "Music Analysis", value: "music_analysis" },
  { label: "Style Selection", value: "style_selection" },
  { label: "Image/Video Generation", value: "generation" },
  { label: "Video Preview & Download", value: "preview_download" },
  { label: "Subscription & Credits", value: "subscription_credits" },
  { label: "New Feature Suggestions", value: "suggestion" },
  { label: "Other", value: "other" },
];

interface FeedbackFormValues {
  category: string;
  threadId?: string;
  feedback: string;
}

export function FeedbackView({ onClose, defaultThreadId }: FeedbackViewProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [form] = Form.useForm<FeedbackFormValues>();
  const [loading, setLoading] = useState(false);
  const [threads, setThreads] = useState<AgentThreadVo[]>([]);
  const [fetchingThreads, setFetchingThreads] = useState(false);

  const { attachmentList, uploadProps, addFiles, onRemove } = useFileUpload({
    maxCount: 5,
    accept: "image/*,video/*",
  });

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData.items;
      const files: File[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (
          item.kind === "file" &&
          (item.type.startsWith("image/") || item.type.startsWith("video/"))
        ) {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        addFiles(files);
      }
    },
    [addFiles],
  );

  const urlThreadId =
    searchParams.get("thread_id") || searchParams.get("thread");

  useEffect(() => {
    const tId = defaultThreadId || urlThreadId;
    if (tId && !form.getFieldValue("threadId")) {
      form.setFieldsValue({ threadId: tId });
    }
  }, [urlThreadId, defaultThreadId, form]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      router.push("/");
    }
  };

  useEffect(() => {
    const loadThreads = async () => {
      setFetchingThreads(true);
      try {
        const list = await fetchUserThreads({ limit: 50, offset: 0 });
        setThreads(list);
      } catch (error) {
        console.error("Failed to fetch threads:", error);
      } finally {
        setFetchingThreads(false);
      }
    };
    loadThreads();
  }, []);

  const handleSubmit = async (values: FeedbackFormValues) => {
    setLoading(true);
    try {
      const urls = await uploadAttachments(attachmentList);

      const debugInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        consoleLogs: consoleCapturer.getLogs(),
      };

      const feedbackContent = {
        category: values.category,
        threadId: values.threadId,
        feedback: values.feedback,
        files: urls,
        debugInfo,
      };

      const request: FeedbackSubmitRequest = {
        // cast to any to support new type "problem_feedback" if not in enum yet
        type: FeedbackSubmitRequestType.Feedback,
        content: JSON.stringify(feedbackContent),
      };

      await api.feedback.submitFeedback(request);
      message.success("Feedback submitted successfully!");
      handleClose();
    } catch (error) {
      console.error("Submit feedback error:", error);
      message.error("Failed to submit feedback, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-[456px] bg-background-elevated rounded-3xl p-6 sm:px-7 sm:py-9 relative">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 bg-transparent border-0 cursor-pointer z-10"
        >
          <CloseIcon className="w-5 h-5 text-text-tertiary hover:text-text-secondary transition-colors" />
        </button>

        <div className="text-center sm:text-left mb-5">
          <h1 className="text-xl font-medium text-text-primary mb-2">
            Feedback & Issues
          </h1>
          <p className="text-text-secondary text-sm">
            We value your feedback. Let us know how we can improve.
          </p>
        </div>

        <ConfigProvider
          theme={{
            token: {
              colorFillTertiary: "#212129",
              colorTextPlaceholder: "rgba(255, 255, 255, 0.40)",
              fontSizeLG: 14,
            },
            components: {
              Form: {
                labelColor: "#fff",
                itemMarginBottom: 12,
                labelFontSize: 14,
                fontSize: 12,
                verticalLabelPadding: 4,
              },
              Button: {
                controlHeight: 48,
                fontWeight: 500,
                fontSize: 14,
                borderRadius: 99,
              },
              Select: {
                borderRadiusLG: 8,
                activeBorderColor: "transparent",
                hoverBorderColor: "transparent",
                controlOutline: "transparent",
                colorFillSecondary: "#31313b",
                colorBorder: "transparent",
              },
              Input: {
                borderRadius: 8,
                paddingBlock: 8,
                paddingInline: 16,
                activeBorderColor: "transparent",
                hoverBorderColor: "transparent",
                controlOutline: "transparent",
              },
            },
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            className="[&_.ant-form-item-label]:!pl-0"
          >
            <Form.Item
              name="category"
              label={<span>Category</span>}
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                placeholder="Select a category"
                size="large"
                options={FEEDBACK_CATEGORIES}
                variant="filled"
                suffixIcon={
                  <span className="translate-x-[-4px]">
                    <ArrowIcon />
                  </span>
                }
                className="[&_.ant-select-selector]:!py-2 [&_.ant-select-selector]:!px-4 [&_.ant-select-selector]:!h-auto"
              />
            </Form.Item>

            <Form.Item name="threadId" label={<span>Project (Optional)</span>}>
              <Select
                placeholder="Select a project"
                size="large"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                loading={fetchingThreads}
                options={threads.map((t) => ({
                  label: t.title || "Untitled Project",
                  value: t.id,
                }))}
                variant="filled"
                allowClear
                disabled={!!defaultThreadId}
                suffixIcon={
                  <span className="translate-x-[-4px]">
                    <ArrowIcon />
                  </span>
                }
                className="[&_.ant-select-selector]:!py-2 [&_.ant-select-selector]:!px-4 [&_.ant-select-selector]:!h-auto"
              />
            </Form.Item>

            <Form.Item
              name="attachments"
              label={<span>Attachment (Optional)</span>}
            >
              <div className="flex flex-wrap items-center gap-3">
                {attachmentList.length > 0 && (
                  <ChatBoxFiles
                    attachmentList={attachmentList}
                    onRemove={onRemove}
                    size="small"
                  />
                )}
                {attachmentList.length < 5 && (
                  <Upload {...uploadProps}>
                    <div className="w-12 h-12 rounded-lg bg-background-card hover:bg-background-card-hover flex items-center justify-center cursor-pointer transition-colors">
                      <PlusIcon className="w-4 h-4 text-text-tertiary" />
                    </div>
                  </Upload>
                )}
              </div>
            </Form.Item>

            <Form.Item
              name="feedback"
              label={<span>Feedback</span>}
              rules={[
                {
                  required: true,
                  message: "Please describe your issue or suggestion",
                },
              ]}
            >
              <Input.TextArea
                placeholder="Tell us more about your experience..."
                rows={4}
                variant="filled"
                className="!bg-background-card !text-text-primary !border-transparent hover:!bg-background-card-hover focus:!bg-background-card-hover focus:!border-transparent focus-within:!border-transparent resize-none rounded-xl py-2 px-4"
                style={{ height: "120px" }}
                onPaste={handlePaste}
              />
            </Form.Item>

            <div className="mt-6">
              <Button type="primary" htmlType="submit" block loading={loading}>
                Submit Feedback
              </Button>
            </div>
          </Form>
        </ConfigProvider>
      </div>
    </div>
  );
}
